import type { ReactElement } from 'react';

/**
 * The editor's icons, drawn as strokes so they look the same on every phone.
 * Emoji were the quick way in and rendered as five different sets on five
 * devices; an editing app needs the scissors to look like the same scissors.
 */
const PATHS: Record<string, string> = {
  back: 'M15 5l-7 7 7 7',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  // „Neu holen“ -- stand bis 7.17 als „⟳“ im Text und sah auf jedem Gerät
  // anders aus, während alle anderen Zeichen aus diesem Satz kommen.
  sync: 'M20 11a8 8 0 0 0-13.7-5.7L4 7.5M4 4v3.5h3.5M4 13a8 8 0 0 0 13.7 5.7L20 16.5M20 20v-3.5h-3.5',
  film: 'M4 4h16v16H4zM4 9h16M4 15h16M9 4v16M15 4v16',
  text: 'M5 6h14M12 6v13M9 19h6',
  captions: 'M3 6h18v12H3zM7 12h4M13 12h4M7 15h10',
  brand: 'M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6-4.5-4.2 6.1-.7z',
  export: 'M12 4v11M7 10l5 5 5-5M4 19h16',
  // Teilen: ein Kopf mit Plus -- jemanden dazuholen.
  teilen: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1M19 8v6M16 11h6',
  settings: 'M4 7h10M18 7h2M4 17h4M12 17h8M14 4.5v5M8 14.5v5',
  scissors: 'M6 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM6 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM20 4L8 15.5M8 8.5L20 20',
  // Nachrücken (S1): ein kurzer Block, eine Lücke, und ein Pfeil, der den
  // hinteren Block in sie hineinzieht. Das Bild soll die Regel sagen, nicht
  // den Fachbegriff -- „Ripple“ steht auf keinem Knopf.
  nachruecken: 'M4 7v10M7 9h4v6H7zM19 9h2v6h-2zM12.5 12h5M15.5 10l2 2-2 2',
  // Gruppieren (S3): zwei Kästchen, von einer gestrichelten Klammer
  // zusammengehalten. Lösen: dieselben zwei, die Klammer offen.
  group: 'M4 4h6v6H4zM14 14h6v6h-6zM10 7h4v4M4 14v6h6',
  ungroup: 'M4 4h6v6H4zM14 14h6v6h-6zM11 6h2M15 6h2M17 8v2',
  copy: 'M9 9h11v11H9zM5 15V4h11',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6',
  // Ändern (7.275): ein Stift, schräg, mit Spitze unten links.
  stift: 'M4 20l1-4L16 5l3 3L8 19zM14 7l3 3',
  volume: 'M4 10v4h4l5 4V6L8 10zM16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11',
  mute: 'M4 10v4h4l5 4V6L8 10zM16 9l5 6M21 9l-5 6',
  play: 'M8 5v14l11-7z',
  pause: 'M7 5h4v14H7zM13 5h4v14h-4z',
  skipBack: 'M6 5v14M18 5L8 12l10 7z',
  skipForward: 'M18 5v14M6 5l10 7-10 7z',
  stepBack: 'M9 5v14M18 6l-8 6 8 6z',
  stepForward: 'M15 5v14M6 6l8 6-8 6z',
  eye: 'M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  eyeOff: 'M3 3l18 18M10 6.5A10 10 0 0 1 22 12s-1.5 2.3-4 4M6 8c-2.5 1.7-4 4-4 4s4 6 10 6c1.4 0 2.7-.3 3.8-.8M9.9 9.9a3 3 0 0 0 4.2 4.2',
  // Schloss zu und Schloss offen, für den Spurkopf: derselbe Körper, nur der
  // Bügel steht anders. Zwei Bilder, die sich auf einen Blick unterscheiden --
  // ein Schloss, das nur „etwas anders“ aussieht, liest niemand im Vorbeigehen.
  schloss: 'M6 11h12v9H6zM9 11V7.5a3 3 0 0 1 6 0V11',
  schlossOffen: 'M6 11h12v9H6zM9 11V7.5a3 3 0 0 1 5.8-1.2',
  sliders: 'M4 6h16M4 12h16M4 18h16M9 4v4M15 10v4M7 16v4',
  transition: 'M3 12h7M14 12h7M10 8l4 4-4 4',
  music: 'M9 18V6l11-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM20 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  check: 'M5 12l5 5 9-10',
  clipboard: 'M9 4h6v3H9zM7 6H5v15h14V6h-2M9 12h6M9 16h6',
  keyframe: 'M12 4.5l5.5 7.5-5.5 7.5-5.5-7.5z',
  mic: 'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v4M8 21h8',
  // Hinweise von Menschen: eine Glocke. Sie hing vorher am Klemmbrett, und
  // das stand daneben fuer „etwas melden“ -- zwei Symbole, die dasselbe
  // sagten (Sascha, 7. September: „Was ist Feedback, was ist Nutzerkommentar“).
  bell: 'M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6zM10 19a2 2 0 0 0 4 0',
  // Und was an uns geht, ist eine Frage: der Kreis mit dem Fragezeichen.
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 0 1 4.9.7c0 1.7-2.4 2-2.4 3.4M12 17.2h.01',
  // Die Verwaltung hatte fünf Punkte und drei Zeichen: „Konten“ und
  // „Diagnose“ trugen beide die Schieberegler, „Ablage“ und „Speicher“ beide
  // das Raster (Sascha, 10. September: „Bitte das Verwaltungs menü aufräumen.
  // Doppelungen“). Jeder Punkt hat jetzt sein eigenes, und jedes sagt, was
  // dahinter liegt.
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M17 10.5a3 3 0 1 0 0-6M18.5 20a6.4 6.4 0 0 0-3-5.2',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  wolke: 'M6.5 18h11a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.6-1.4A3.5 3.5 0 0 0 6.5 18z',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  speicher: 'M4 6c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2zM4 6v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2',
  puls: 'M3 12h4l2-5 3 10 2.5-7 1.5 2h5',
  // Bildmessgeräte (S15): fünf Balken verschiedener Höhe -- ein Histogramm,
  // wie es auf jedem Messgerät steht.
  messgeraet: 'M4 20v-6M8 20V8M12 20v-9M16 20V4M20 20v-7',
  // Raster und Schutzbereich (S25): die beiden Drittel-Linien je Achse -- das
  // Bild, das man vom Drittel-Raster im Kopf hat.
  raster: 'M3 9h18M3 15h18M9 3v18M15 3v18',
  // Suchen und ausführen (S27): die Lupe, die jeder kennt.
  suchen: 'M10.5 17a6.5 6.5 0 100-13 6.5 6.5 0 000 13zM20 20l-4.9-4.9',
  fullscreen: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  fullscreenExit: 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5',
  // Drei Punkte für „Mehr“ (Sascha, 12. Oktober: vorher stand dort das
  // Zeichen fürs Kopieren). Kleine Kreise, damit sie mit dem Strich als
  // Punkte erscheinen.
  mehr: 'M5 12a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0M10.8 12a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0M16.6 12a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0',
};

export function Icon({ name, size = 22 }: { name: keyof typeof PATHS | string; size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      // Damit der Durchgang nicht nur die Beschriftung sieht, sondern auch,
      // welches Zeichen dort steht -- der Haken auf „weg“ fiel sonst nicht auf.
      data-icon={name}
      fill={name === 'play' || name === 'pause' || name === 'brand' ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={name === 'play' || name === 'pause' ? 0 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name] ?? ''} />
    </svg>
  );
}
